import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal from "../../../components/Modal/Modal";

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/webp",
    "image/gif",
    "image/avif",
];

const getCover = (uniq) => {
    try {
        return uniq ? localStorage.getItem(`cover:${uniq}`) || "" : "";
    } catch {
        return "";
    }
};
const setCover = (uniq, dataUrl) => {
    if (!uniq || !dataUrl) return;
    try {
        localStorage.setItem(`cover:${uniq}`, dataUrl);
    } catch { }
    try {
        window.dispatchEvent(new CustomEvent("cover:changed", { detail: { projectId: uniq } }));
    } catch { }
};
const removeCover = (uniq) => {
    if (!uniq) return;
    try {
        localStorage.removeItem(`cover:${uniq}`);
    } catch { }
    try {
        window.dispatchEvent(new CustomEvent("cover:changed", { detail: { projectId: uniq } }));
    } catch { }
};

const getColor = (uniq) => {
    try {
        return uniq ? localStorage.getItem(`color:${uniq}`) || "" : "";
    } catch {
        return "";
    }
};
const setColor = (uniq, color) => {
    if (!uniq || !color) return;
    try {
        localStorage.setItem(`color:${uniq}`, color);
    } catch { }
    try {
        window.dispatchEvent(new CustomEvent("cover:changed", { detail: { projectId: uniq } }));
    } catch { }
};
const removeColor = (uniq) => {
    if (!uniq) return;
    try {
        localStorage.removeItem(`color:${uniq}`);
    } catch { }
    try {
        window.dispatchEvent(new CustomEvent("cover:changed", { detail: { projectId: uniq } }));
    } catch { }
};

const getTitle = (uniq) => {
    try {
        return uniq ? localStorage.getItem(`title:${uniq}`) || "" : "";
    } catch {
        return "";
    }
};
const setTitle = (uniq, title) => {
    if (!uniq || !title) return;
    try {
        localStorage.setItem(`title:${uniq}`, title);
    } catch { }
    try {
        window.dispatchEvent(new CustomEvent("project:changed", { detail: { projectId: uniq } }));
    } catch { }
};
const removeTitle = (uniq) => {
    if (!uniq) return;
    try {
        localStorage.removeItem(`title:${uniq}`);
    } catch { }
    try {
        window.dispatchEvent(new CustomEvent("project:changed", { detail: { projectId: uniq } }));
    } catch { }
};

const validateCoverFile = (file) => {
    if (!file) return { ok: false, message: "Dosya bulunamadı." };
    if (!ALLOWED_TYPES.includes(file.type)) {
        return { ok: false, message: "Lütfen geçerli bir görsel dosyası seçin (JPG, PNG, WEBP, GIF, AVIF)." };
    }
    if (file.size > MAX_FILE_SIZE) {
        return { ok: false, message: "Dosya boyutu 5MB'ı aşmamalı." };
    }
    return { ok: true, message: "" };
};

const readFileAsDataUrl = (file) => {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.readAsDataURL(file);
    });
};

const clampByte = (n) => {
    const v = Math.round(Number(n) || 0);
    if (v < 0) return 0;
    if (v > 255) return 255;
    return v;
};

const rgbToHex = (r, g, b) => {
    const rr = clampByte(r).toString(16).padStart(2, "0");
    const gg = clampByte(g).toString(16).padStart(2, "0");
    const bb = clampByte(b).toString(16).padStart(2, "0");
    return `#${rr}${gg}${bb}`.toUpperCase();
};

const getDominantColorFromDataUrl = async (dataUrl) => {
    try {
        const img = new Image();
        img.decoding = "async";
        img.crossOrigin = "anonymous";
        img.src = dataUrl;

        await new Promise((resolve, reject) => {
            img.onload = () => resolve(true);
            img.onerror = () => reject(new Error("Görsel yüklenemedi"));
        });

        const targetW = 48;
        const ratio = img.width > 0 ? img.height / img.width : 1;
        const targetH = Math.max(24, Math.min(48, Math.round(targetW * ratio)));

        const canvas = document.createElement("canvas");
        canvas.width = targetW;
        canvas.height = targetH;

        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) return "";

        ctx.drawImage(img, 0, 0, targetW, targetH);

        const { data } = ctx.getImageData(0, 0, targetW, targetH);

        let rSum = 0;
        let gSum = 0;
        let bSum = 0;
        let count = 0;

        for (let i = 0; i < data.length; i += 4) {
            const a = data[i + 3];
            if (a < 18) continue;

            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];

            const max = Math.max(r, g, b);
            const min = Math.min(r, g, b);
            const sat = max - min;

            if (sat < 12 && max > 240) continue;

            rSum += r;
            gSum += g;
            bSum += b;
            count += 1;
        }

        if (!count) return "";

        const rAvg = rSum / count;
        const gAvg = gSum / count;
        const bAvg = bSum / count;

        const boost = 1.06;
        const r2 = Math.min(255, rAvg * boost);
        const g2 = Math.min(255, gAvg * boost);
        const b2 = Math.min(255, bAvg * boost);

        return rgbToHex(r2, g2, b2);
    } catch {
        return "";
    }
};

const EditProjectModal = ({ open, close, projectId, projectName, onSaveTitle }) => {
    const [coverPreview, setCoverPreview] = useState("");
    const [colorValue, setColorValue] = useState("");
    const [titleValue, setTitleValue] = useState("");
    const [savingTitle, setSavingTitle] = useState(false);

    const [isDragActive, setIsDragActive] = useState(false);
    const fileInputRef = useRef(null);

    const [colorTouched, setColorTouched] = useState(false);
    const [autoColor, setAutoColor] = useState("");

    useEffect(() => {
        if (!open) return;

        const currentCover = getCover(projectId);
        const currentColor = getColor(projectId) || "";
        const currentTitle = getTitle(projectId) || projectName || "";

        setCoverPreview(currentCover);
        setColorValue(currentColor);
        setTitleValue(currentTitle);

        setIsDragActive(false);
        setColorTouched(Boolean(currentColor));
        setAutoColor("");

        if (currentCover && !currentColor) {
            (async () => {
                const picked = await getDominantColorFromDataUrl(currentCover);
                if (!picked) return;
                setAutoColor(picked);
                setColorValue(picked);
                setColorTouched(false);
            })();
        }
    }, [open, projectId, projectName]);

    const openFilePicker = () => {
        if (fileInputRef.current) fileInputRef.current.click();
    };

    const applyAutoColorIfAllowed = async (dataUrl) => {
        const picked = await getDominantColorFromDataUrl(dataUrl);
        if (!picked) return;

        setAutoColor(picked);
        if (!colorTouched) {
            setColorValue(picked);
        }
    };

    const setCoverFromFile = async (file) => {
        const check = validateCoverFile(file);
        if (!check.ok) {
            alert(check.message);
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        const dataUrl = await readFileAsDataUrl(file);
        setCoverPreview(dataUrl);
        await applyAutoColorIfAllowed(dataUrl);
    };

    const onPickCover = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        await setCoverFromFile(file);
    };

    const onDropCover = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);

        const file = e.dataTransfer?.files?.[0];
        if (!file) return;

        await setCoverFromFile(file);
    };

    const onDragOverCover = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isDragActive) setIsDragActive(true);
    };

    const onDragLeaveCover = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
    };

    const onKeyDownDropzone = (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openFilePicker();
        }
    };

    const saveCover = () => {
        if (coverPreview) setCover(projectId, coverPreview);
    };

    const clearCover = () => {
        removeCover(projectId);
        setCoverPreview("");

        if (!colorTouched && autoColor && colorValue === autoColor) {
            setColorValue("");
        }

        setAutoColor("");
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const saveColor = () => {
        if (colorValue) setColor(projectId, colorValue);
    };

    const clearColor = async () => {
        removeColor(projectId);
        setColorValue("");
        setColorTouched(false);

        if (coverPreview) {
            setAutoColor("");
            await applyAutoColorIfAllowed(coverPreview);
        } else {
            setAutoColor("");
        }
    };

    const onUserSetColor = (value) => {
        setColorTouched(true);
        setColorValue(value);
    };

    const saveTitleHandler = async () => {
        const next = String(titleValue || "").trim();
        if (!next || !projectId) return;

        setSavingTitle(true);

        let success = true;
        try {
            if (typeof onSaveTitle === "function") {
                await onSaveTitle(projectId, next);
            } else {
                setTitle(projectId, next);
            }
        } catch {
            success = false;
        }

        setSavingTitle(false);

        if (success) {
            close();
        }
    };

    const resetTitleHandler = () => {
        setTitleValue(projectName || "");
        if (typeof onSaveTitle !== "function") {
            removeTitle(projectId);
        }
    };

    const headerTitle = String(titleValue || projectName || "").trim();

    const isAutoApplied = useMemo(() => {
        return Boolean(coverPreview && autoColor && colorValue === autoColor && !colorTouched);
    }, [coverPreview, autoColor, colorValue, colorTouched]);

    return (
        <Modal open={open} close={close}>
            <h2>{headerTitle} — Düzenle</h2>

            <div className="title-field">
                <label className="title-label">Proje Başlığı</label>
                <div className="title-row">
                    <input
                        type="text"
                        value={titleValue}
                        onChange={(e) => setTitleValue(e.target.value)}
                        placeholder="Proje başlığı"
                        className="title-input"
                    />
                    <button
                        className="modal-add-button"
                        onClick={saveTitleHandler}
                        disabled={!String(titleValue || "").trim() || savingTitle}
                    >
                        {savingTitle ? "Kaydediliyor..." : "Başlığı Kaydet"}
                    </button>
                    <button className="modal-close-button danger" onClick={resetTitleHandler}>
                        Başlığı Sıfırla
                    </button>
                </div>
            </div>

            <div className="cover-field">
                <div className="cover-field-head">
                    <div className="cover-field-title">Kapak</div>
                    {coverPreview && (
                        <button className="cover-mini-link" type="button" onClick={clearCover}>
                            Kaldır
                        </button>
                    )}
                </div>

                <div
                    className={`cover-dropzone${isDragActive ? " drag-active" : ""}${coverPreview ? " has-preview" : ""}`}
                    role="button"
                    tabIndex={0}
                    aria-label="Kapak fotoğrafı yükle. Sürükleyip bırakabilir veya tıklayabilirsin."
                    onClick={openFilePicker}
                    onKeyDown={onKeyDownDropzone}
                    onDrop={onDropCover}
                    onDragOver={onDragOverCover}
                    onDragLeave={onDragLeaveCover}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="cover-file-input"
                        onChange={onPickCover}
                    />

                    {!coverPreview && (
                        <div className="dropzone-inner">
                            <div className="dropzone-icon" aria-hidden="true">⬆</div>
                            <div className="dropzone-title">Sürükle &amp; Bırak</div>
                            <div className="dropzone-desc">
                                veya <span className="dropzone-cta">dosya seç</span>
                            </div>
                            <div className="dropzone-meta">JPG, PNG, WEBP, GIF, AVIF • Maks. 5MB</div>
                        </div>
                    )}

                    {coverPreview && (
                        <div className="cover-preview-advanced">
                            <img src={coverPreview} alt="Kapak önizleme" />
                            <div className="cover-preview-actions">
                                <button
                                    type="button"
                                    className="cover-action-btn"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        openFilePicker();
                                    }}
                                >
                                    Değiştir
                                </button>

                                <button
                                    type="button"
                                    className="cover-action-btn danger"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        clearCover();
                                    }}
                                >
                                    Kaldır
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="cover-actions">
                    <button className="modal-add-button" onClick={saveCover} disabled={!coverPreview}>
                        Kapağı Kaydet
                    </button>
                    <button className="modal-close-button danger" onClick={clearCover} disabled={!coverPreview}>
                        Kapağı Kaldır
                    </button>
                </div>
            </div>

            <div className="color-field">
                <div className="color-field-head">
                    <label>Proje Rengi</label>

                    {isAutoApplied && (
                        <div className="auto-color-badge" title="Kapak fotoğrafından otomatik alındı">
                            <span className="auto-color-dot" style={{ backgroundColor: colorValue }} />
                            Kapaktan seçildi
                        </div>
                    )}
                </div>

                <div>
                    <input
                        type="color"
                        value={colorValue || "#489bf4"}
                        onChange={(e) => onUserSetColor(e.target.value)}
                        title="Renk seç"
                    />
                    <input
                        type="text"
                        placeholder="#RRGGBB"
                        value={colorValue}
                        onChange={(e) => onUserSetColor(e.target.value)}
                    />
                    <button className="modal-add-button" onClick={saveColor} disabled={!colorValue}>
                        Rengi Kaydet
                    </button>
                    <button className="modal-close-button danger" onClick={clearColor}>
                        Rengi Kaldır
                    </button>
                </div>
            </div>

            <div className="editmodal-footer">
                <button className="modal-secondary-button" onClick={close}>
                    Kapat
                </button>
            </div>
        </Modal>
    );
};

export default EditProjectModal;
